import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Alert, App, Divider, Flex, Pagination, Segmented, Space, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import Button from "../../Components/Button"
import DashboardCard from "../../Components/DashboardCard"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ListViewToggle from "../../Components/ListViewToggle/ListViewToggle"
import ManageColumn from "../../Components/ManageColumns"
import MobilePagination from "../../Components/MobilePagination/MobilePagination"
import Table, { itemRender } from "../../Components/Table"
import { getBaseColumns } from "../../Components/Table/baseColumns"
import { useTitleSectionAction } from "../../Components/TitleSection/TitleSectionContext"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import {
  creditsColumnIconMap,
  defaultFiltersByFolders,
  removeQuery,
  serializeData
} from "../../Utils/utils"
import { CreditRequestModal } from "../AlertsPage/components/CreditRequestModal"
import {
  addGlobalFilter as addGlobalFilterAction,
  clearGlobalFilters as clearGlobalFiltersAction,
  removeGlobalFilter as removeGlobalFilterAction
} from "../GlobalFilters/redux/globalFilters.slice"
import RequestsCreators, { selectRequestResult } from "../RequestsPage/reducer"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import { useUpdatePreferences } from "../User/hooks/useUpdatePreferences"
import { userSave } from "../User/redux/user.thunks"
import { AutomationStatusButton } from "./components/AutomationStatusButton"
import { Banner } from "./components/Banner"
import { ChangeStatusBlock } from "./components/ChangeStatusBlock"
import CreditDetailsDrawer from "./components/CreditDetailsDrawer/CreditDetailsDrawer"
import { CreditDrawer } from "./components/Drawer"
import CreditsList from "./components/List/CreditsList"
import { AutomateCreditsModal } from "./components/Modal/AutomateCreditsModal"
import { CreditRequestPreview } from "./components/Modal/CreditRequestPreview"
import { RejectRequestModal } from "./components/Modal/RejectRequestModal"
import { ShopifyActivateAccessModal } from "./components/Modal/ShopifyActivateAccessModal"
import { NotifyMerchantCheckbox } from "./components/NotifyMerchantCheckbox"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import { creditAutomationActions } from "./redux/creditAutomation.slice"
import {
  confirmCreditAutomationAccess,
  getCreditAutomation,
  requestCreditAutomation,
  turnOffCreditAutomation
} from "./redux/creditAutomation.thunks"
import {
  CREDITS_PREFERENCES_VERSION,
  creditsActions,
  selectAreFiltersDefault
} from "./redux/credits.slice"
import {
  deleteCredit,
  getCreditDetails,
  getCredits,
  getCreditsExport,
  getCreditsFilterOptions,
  getCreditsStatistics,
  pinCredit,
  updateCredit
} from "./redux/credits.thunks"
//import {extendedAccessRoles} from '../../Utils/permission'
import { useCreditsPageQuery } from "./useCreditsPageQuery"
import { buildNotifyPayload, isNotifiableStatusChange } from "./utils/notify"

import "./styles.less"

const statisticData = [
  {
    id: "all",
    name: "All",
    brand: null,
    value: 0,
    isConnected: true
  },
  {
    id: "manual",
    name: "Manual (by you)",
    shortName: "Manual",
    brand: null,
    value: 0,
    isConnected: true
  },
  {
    id: "system",
    name: "System (by us)",
    shortName: "System",
    brand: null,
    value: 0,
    isConnected: true
  }
]

const statisticDataAdmin = [
  {
    id: "all",
    name: "All",
    brand: null,
    value: 0,
    isConnected: true
  },
  {
    id: "Ethoca",
    name: "Ethoca",
    img: "ethoca",
    brand: "mastercard",
    value: 0,
    isConnected: true
  },
  /*{
    id: 'Verifi',
    name: 'Verifi',
    img: 'rdr',
    brand: 'visa',
    value: 0,
    isConnected: true,
  },*/
  {
    id: "RDR",
    name: "RDR",
    img: "rdr",
    brand: "visa",
    value: 0,
    isConnected: true
  },
  {
    id: "CDRN",
    name: "CDRN",
    img: "cdrn",
    brand: "visa",
    value: 0,
    isConnected: true
  }
]

const pageSize = 50

const optionsVisibility = [
  { value: "all", label: "All" },
  { value: "System", label: "System" },
  { value: "Manual", label: "Manual" }
]

const pinnedColumns = ["Request status"]
const pinnedColumnsAdmin = ["Request type"]

const exportItemsAdmin = [
  {
    key: "all",
    label: "Export all"
  },
  {
    key: "as_filtered",
    label: "Export as filtered"
  },
  {
    key: "ethoca_pending",
    label: "Ethoca Pending by period"
  },
  {
    key: "verifi_pending",
    label: "Verifi Pending by period"
  }
]

const exportItemsUser = [
  {
    key: "all",
    label: "Export all"
  },
  {
    key: "as_filtered",
    label: "Export as filtered"
  }
]

const { Text, Link } = Typography

const defaultFiltersByFolder = defaultFiltersByFolders["credits"]

// CHA1-2308: the backend's aggregate `status` is Title Case ("Not requested", "Pending", "Action
// needed", "Active", "Lost", "Disabled") - map it to this repo's existing internal keys. "Active"
// additionally splits into "active"/"active_stripe_only" depending on whether Stripe is the only
// connected processor (CHA1-3088: no dropdown/Turn off when there's nothing else to manage).
const AUTOMATION_STATUS_MAP = {
  "Not requested": "not_started",
  Pending: "pending",
  "Action needed": "action_needed",
  Active: "active",
  Lost: "lost",
  Disabled: "disabled"
}

export const getInternalAutomationStatus = ({ status, processors }) => {
  const mapped = AUTOMATION_STATUS_MAP[status] || null
  if (mapped !== "active") return mapped
  const isStripeOnly = processors?.length === 1 && processors[0]?.service_name === "stripe"
  return isStripeOnly ? "active_stripe_only" : "active"
}

// Pure, unit-testable: decides which automation button variant (if any) belongs in the header.
// The internal status already names the variant 1:1 - the only real condition is gating the
// "Automate credits" button (shown for "not_started" and "disabled") behind `can_request`
// (CHA1-3086/CHA1-3198), plus hiding everything for admins.
// `has_access === false` means the account has no access to the feature at all (CHA1-3565) - no
// button in any state. `can_request` already implies has_access, so this is an explicit safety net
// matching the documented contract; `undefined` (before the state loads) must NOT hide anything.
export const getAutomationButtonVariant = ({ isAdmin, automationState, canRequest, hasAccess }) => {
  if (isAdmin) return null
  if (hasAccess === false) return null
  if ((automationState === "not_started" || automationState === "disabled") && !canRequest) {
    return null
  }
  return automationState
}

// The Banner only shows one reason at a time - pick the first processor whose row explains why the
// aggregate status isn't Active (Action needed/Lost), matching the aggregation order documented by
// the backend (CHA1-2308).
// Prefer a Lost/Action needed processor (matching the backend's aggregation precedence); only fall
// back to a Pending one when nothing is in a worse state - so the Shopify Pending banner (CHA1-3281)
// gets its processor without overriding a more severe status.
export const getBannerProcessor = (processors) =>
  processors?.find(
    (processor) => processor.status === "Lost" || processor.status === "Action needed"
  ) || processors?.find((processor) => processor.status === "Pending")

const CreditsPage = ({
  usersViewForAdmin,
  nav: { isMobile, theme },
  isAdmin,
  businessAccountId,
  ba_role,
  ba_role_name,
  visibleFiltersUser,
  visibleFiltersAdmin,
  defaultColumns,
  defaultColumnsAdmin,
  columns,
  columnsAdmin,
  fetchingStatistics,
  statistics,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  saveResult,
  test_data,
  is_demo,
  creditsViewMode,
  setCreditsViewMode,
  ...props
}) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const externalDemoMode = useExternalDemoMode()
  const { setAction } = useTitleSectionAction()
  const dispatch = useDispatch()

  const { business_account_id, folder, baId } = useCreditsPageQuery(search)
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [rejectModalVisible, setRejectModalVisible] = useState(null)
  const [selectedRequestId, setSelectedRequestId] = useState([])
  const [automateCreditsModalOpen, setAutomateCreditsModalOpen] = useState(false)
  const [shopifyActivateOpen, setShopifyActivateOpen] = useState(false)
  const {
    status: automationApiStatus,
    processors: automationProcessors,
    can_request: canRequest,
    is_eligible: isEligible,
    has_access: hasAccess,
    requestFetching: automationRequestFetching,
    requestResult: automationRequestResult,
    confirmFetching: automationConfirmFetching,
    turnOffFetching: automationTurnOffFetching
  } = useSelector((state) => state.creditAutomation)
  const isMulti = !!selectedRequestId?.length
  const isFiltersDefault = useSelector((state) => selectAreFiltersDefault(state, folder))
  const requestResult = useSelector(selectRequestResult)
  const { isShowDemoData } = useDemoDataToggle()
  const { count, results, statistic } = result

  const effectiveViewMode = isMobile ? "cards" : creditsViewMode

  const isAdminPage = isAdmin && !usersViewForAdmin
  // CreditsPage is always mounted as <CreditsPage /> (no usersViewForAdmin), so the viewed-account
  // branch was dead and referenced an undefined `businessAccount`. Fall back to the user's own
  // is_demo. TODO: wire the viewed BA's is_demo here if admin user-view of Credits is ever enabled.
  const isDemo = is_demo
  const isDemoModeBlocked = !isAdmin && !businessAccountId
  const isBusinessAccountAdministrator = ba_role_name === "Administrator"

  const myColumns = isAdminPage ? columnsAdmin : columns
  const allFilters = isAdminPage ? defaultColumnsAdmin : defaultColumns
  const visibleFilters = isAdminPage ? visibleFiltersAdmin : visibleFiltersUser
  const businessAccountRole = isAdmin ? null : ba_role
  const automationState = getInternalAutomationStatus({
    status: automationApiStatus,
    processors: automationProcessors
  })
  const bannerStatus = automationState === "active_stripe_only" ? "active" : automationState
  const bannerProcessor = getBannerProcessor(automationProcessors)
  const shopifyProcessor = automationProcessors?.find(
    (processor) => processor.service_name === "shopify"
  )

  const adminParams = usersViewForAdmin ? { business_account_id } : {}

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const preferencesData = useMemo(
    () => ({
      version: CREDITS_PREFERENCES_VERSION,
      filters,
      columns,
      columnsAdmin,
      visibleFilters: visibleFiltersUser,
      visibleFiltersAdmin
    }),
    [filters, columns, columnsAdmin, visibleFiltersUser, visibleFiltersAdmin]
  )
  useUpdatePreferences("credits", preferencesData)

  const statisticCardsData = useMemo(() => {
    return [
      {
        title: "Needs attention",
        tooltip: "Credit requests that require action",
        value: statistics?.needs_attention || 0
      },
      {
        title: "Pending review",
        tooltip: "Number of credits pending review",
        value: statistics?.pending_review || 0
      },
      {
        title: "Alerts credited",
        tooltip: "Number of approved credit requests",
        value: statistics?.alerts_credited || 0
      },
      {
        title: "Alert fees credited",
        prefix: "$",
        tooltip: "Total dollar value from approved credit requests",
        value: statistics?.alert_fees_credited || 0
      }
    ]
  }, [statistics])

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  if (baId && baId !== businessAccountId) {
    props.changeBusinessAccount(baId)
    removeQuery("id", navigate)
  }

  useEffect(() => {
    document.title = isAdmin ? "Requests Credits - Chargeback" : "Credit requests - Chargeback"
  }, [isAdmin])

  useEffect(() => {
    if (pageNumber !== 1) setPageNumber(1)
    props.getCreditsStatistics(adminParams)
    props.getCreditsFilterOptions(adminParams)
  }, [businessAccountId])

  useEffect(() => {
    if (!isAdmin) dispatch(getCreditAutomation())
  }, [isAdmin, businessAccountId])

  useEffect(() => {
    if (folder) {
      props.changeFilters(defaultFiltersByFolder[folder])
      if (pageNumber !== 1) setPageNumber(1)
      if (isMulti) setSelectedRequestId([])
    } else {
      props.changeFilters({ provider_status: "all", is_archived: null })
    }
  }, [folder])

  const filterOptionsModifiedByFolder = useMemo(
    () => ({
      ...filterOptions,
      provider_status:
        !folder || defaultFiltersByFolder[folder]?.provider_status === "all"
          ? filterOptions?.provider_status
          : defaultFiltersByFolder[folder]?.provider_status,
      credits_automation: ["Automated", "Not automated"]
    }),
    [filterOptions, folder]
  )

  const handleClearFilters = useCallback(() => {
    const newFilters = folder
      ? defaultFiltersByFolder[folder]
      : { provider_status: "all", is_archived: null }
    props.clearCreditsFilters(newFilters)
    props.clearGlobalFilters()
  }, [folder])

  const updateData = () => {
    const serializeFilters = serializeData(filters)
    const defaultColumns = [
      "id",
      "is_archived",
      "transaction_link",
      "currency",
      "converted_currency",
      "converted_amount",
      "brand",
      "proof_deadline",
      "rejection_reason_details",
      "rejection_reason"
    ]
    const defaultColumnsUser = [...defaultColumns, "pinned", "request_status"]
    const defaultColumnsAdmin = [...defaultColumns, "request_type"]
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)

    const data = {
      page: pageNumber,
      page_size: pageSize,
      ...adminParams,
      ordering,
      ...serializeFilters,
      ...(isAdminPage ? { test_data } : {}),
      ...(columns.length && {
        columns: [...(isAdminPage ? defaultColumnsAdmin : defaultColumnsUser), ...columns].join(",")
      })
    }

    props.getCredits({ ...data })
  }

  const prevTestDataRef = useRef(test_data)
  const notifyRef = useRef(true)

  useEffect(() => {
    if (requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    updateData()
  }, [
    businessAccountId,
    ordering,
    pageNumber,
    filters,
    test_data,
    myColumnsLength,
    requestResult,
    isShowDemoData
  ])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: t("Exporting"),
        description: exportResult?.message, //t('Export in progress, you will be notified once it's done.'),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  useEffect(() => {
    if (saveResult) {
      if (saveResult === "credit_details_export") {
        notification.success({
          message: t("Your credit has been successfully downloaded."),
          ...notificationConfig
        })
      }
      if (saveResult?.message) {
        notification.success({
          message: saveResult?.message,
          ...notificationConfig
        })
      }
      if (saveResult?.type === "credit_update" && selectedRequestId?.length)
        setSelectedRequestId([])
      if (saveResult?.type === "credit_update" && rejectModalVisible) setRejectModalVisible(null)
      if (saveResult !== "credit_details_export") {
        props.getCreditsStatistics(adminParams)
        updateData()
      }
      props.saveResultReset()
    }
  }, [saveResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        //x: (220 * myColumns.filter(({active}) => active).length),
        x: "max-content",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statistic?.all ? (
          <EmptyComponent type={isAdmin ? "requests_credits_admin" : "credits"} />
        ) : (
          <EmptyComponent type={isAdmin ? "requests_credits_admin" : "search"}>
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={handleClearFilters}
            >
              {t("Clear search")}
            </Button>
          </EmptyComponent>
        )
      },
      rowSelection: {
        type: "checkbox",
        columnWidth: 54,
        selectedRowKeys: selectedRequestId,
        onChange: (newSelectedRowKeys, selectedRows) => {
          setSelectedRequestId(newSelectedRowKeys)
        },
        getCheckboxProps: (record) => ({
          disabled: record.name === "Disabled User",
          // Column configuration not to be checked
          name: record.name
        })
      }
    }),
    [myColumnsLength, count, pageNumber, selectedRequestId]
  )

  const getTableColumns = useMemo(() => {
    const handleChangeStatus = ({ id, status_type, status_value }) => {
      if (status_type === "request_status" && status_value?.toLowerCase() === "rejected") {
        setRejectModalVisible(id)
        return
      }

      // provider status changes that send an email to the merchant are confirmed first,
      // so the admin can opt out of the notification
      if (isNotifiableStatusChange({ status_type, status_value })) {
        notifyRef.current = true

        modal.confirm({
          ...modalConfig("info"),
          centered: !isMobile,

          title: "Provider status",
          content: (
            <>
              <Text type="secondary">
                Are you sure you want to change Provider status? Note that changing Provider status
                may affect Request status value and credit balance for corresponding business
                account.
              </Text>
              <div style={{ marginTop: 16 }}>
                <NotifyMerchantCheckbox onChange={(checked) => (notifyRef.current = checked)} />
              </div>
            </>
          ),
          cancelText: "Go back",
          okText: "Change status",
          cancelButtonProps: { size: "large" },
          okButtonProps: { size: "large" },
          onOk() {
            props.updateCredit({
              id,
              status_type,
              status_value,
              ...buildNotifyPayload(notifyRef.current)
            })
          }
        })
        return
      }

      props.updateCredit({ id, status_type, status_value })
    }

    let options = filterOptions || {}

    let allColumns = {
      ...getBaseColumns({ t, isAdmin: isAdminPage, onSelectRow: handleSelectRow }),
      ...getColumns({
        t,
        options,
        handleChangeStatus,
        theme,
        isAdmin: isAdminPage,
        onSelectRow: handleSelectRow,
        disabledStatus: !isAdmin || isMulti
      })
    }

    const columns = [
      allColumns[isAdmin ? "request_type" : "request_status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data, t })),
      ...getActionColumn({
        onSelectRow: handleSelectRow,
        isAdmin,
        disabled: isMulti,
        isDemo: isDemoModeBlocked || isDemo,
        isBusinessAccountAdministrator: ba_role_name === "Administrator"
      })
    ]

    const userColumns = [allColumns["icon"], ...columns]
    return isAdmin ? columns : userColumns
  }, [
    myColumnsHash,
    filterOptions,
    theme,
    isAdmin,
    isMulti,
    search,
    ba_role_name,
    isDemoModeBlocked,
    isDemo,
    isMobile
  ])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback(
    (newData, type) => {
      if (newData?.user_email) {
        if (Array.isArray(newData.user_email) && newData.user_email.length > 0) {
          newData.user_email.forEach((email) => {
            if (email) props.addGlobalFilter("user_email", email.trim())
          })
        } else if (typeof newData.user_email === "string" && newData.user_email.trim()) {
          props.addGlobalFilter("user_email", newData.user_email.trim())
        } else {
          props.removeGlobalFilter("user_email")
        }
      }
      if (type === "changeVisible") {
        props.changeVisibleFilters(isAdminPage ? "visibleFiltersAdmin" : "visibleFilters", newData)
      } else {
        props.changeFilters(newData)
        setPageNumber(1)
      }
    },
    [isAdminPage]
  )

  const handleChangeColumns = useCallback(
    (newColumns) => {
      props.changeColumns(isAdminPage ? "columnsAdmin" : "columns", newColumns)
    },
    [isAdminPage]
  )

  const handleSelectType = (value, keyFilter) => {
    let newFilterValue = null
    let cardsLength = isAdmin ? statisticDataAdmin?.length : statisticData.length
    if (value === "all") {
      newFilterValue = "all"
    } else {
      const currentAlertService = filters?.[keyFilter] || []
      if (currentAlertService === "all") {
        newFilterValue = [value]
      } else {
        if (currentAlertService?.includes(value)) {
          newFilterValue = currentAlertService.filter((item) => item !== value)
        } else {
          newFilterValue = [...currentAlertService, value]
          if (newFilterValue?.length === cardsLength - 1) newFilterValue = "all"
        }
      }
    }
    handleChangeFilter({ [keyFilter]: newFilterValue })
  }

  const openAlertDetails = (creditData, attach) => {
    const isRejected = creditData?.request_status === "Rejected"
    const isManual = creditData?.request_type === "Manual"
    if (creditData?.id) {
      let attachParams = attach ? "&attach=true" : ""
      if (usersViewForAdmin && search) {
        navigate(`${search}&open_credit_request=${creditData?.id}${attachParams}`)
      } else if (isAdmin) {
        navigate(`?open_credit=${creditData?.id}${attachParams}`)
      } else if (isRejected && isManual) {
        props.getCreditDetailsRequest(creditData?.id)
        navigate(`?open_credit_resubmit=${creditData?.id}`)
      } else {
        navigate(`?open_credit_request=${creditData?.id}${attachParams}`)
      }
    }
  }

  function handleSelectRow(data, type) {
    //props.changeBusinessAccount(newBusinessAccountId)
    let business_account_id =
      data?.business_account?.business_account_id || data?.business_account?.id
    if (type === "business_account") {
      if (business_account_id) {
        window.open(
          `${process.env.REACT_APP_BASE_URI}/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`
        )
      }
      //setRedirect('/business_settings/my_business')
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "pin") {
      props.pinCredit({ id: data?.["id"], pin: !data?.pinned })
    } else if (type === "preview" || type === "view_details") {
      openAlertDetails(data)
    } else if (type === "view_cr_details") {
      navigate(`?open_credit_details=${data?.id}`)
    } else if (type === "attach") {
      openAlertDetails(data, true)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Confirm deleting",
        content: "Are you sure you want to delete this request? This action can NOT be undone.",

        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteCredit(data.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "archive") {
      props.updateCredit({ id: data.id, is_archived: !data?.["is_archived"] })
    } else if (type === "add_comment") {
      props.selectRequestAddComment(data)
      const searchParams = new URLSearchParams(search)
      searchParams.set("add_comment", data?.id)
      navigate(`?${searchParams.toString()}`)
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getCreditsExport({
        isAdmin: isAdminPage,
        export_type: type,
        file_name: "Credits report",
        ...(type === "as_filtered" && serializeFilters),
        ...(isAdminPage && type !== "all" ? { test_data } : {})
      })
    },
    [filters, test_data]
  )

  const automationButtonVariant = useMemo(
    () => getAutomationButtonVariant({ isAdmin, automationState, canRequest, hasAccess }),
    [isAdmin, automationState, canRequest, hasAccess]
  )

  const handleConfirmAccess = useCallback(
    (serviceName) => dispatch(confirmCreditAutomationAccess(serviceName)),
    [dispatch]
  )

  const handleTurnOff = useCallback(async () => {
    const result = await dispatch(turnOffCreditAutomation())
    if (turnOffCreditAutomation.fulfilled.match(result)) {
      notification.success({
        message: result.payload?.detail || t("Credit Automation has been turned off"),
        ...notificationConfig
      })
    }
  }, [dispatch, notification, t])

  const statusButton = useMemo(() => {
    switch (automationButtonVariant) {
      case "active":
      case "active_stripe_only":
        return (
          <AutomationStatusButton
            processors={automationProcessors}
            isStripeOnly={automationButtonVariant === "active_stripe_only"}
            onConfirmAccess={handleConfirmAccess}
            confirmFetching={automationConfirmFetching}
            onTurnOff={handleTurnOff}
            turnOffFetching={automationTurnOffFetching}
          />
        )

      case "pending":
        return (
          <AutomationStatusButton
            processors={automationProcessors}
            variant="processing"
            onConfirmAccess={handleConfirmAccess}
            confirmFetching={automationConfirmFetching}
          />
        )

      case "action_needed":
      case "lost":
        return (
          <Button
            type="primary"
            icon={<Icon type="automation_loop" style={{ filter: "brightness(0) invert(1)" }} />}
            onClick={() => setAutomateCreditsModalOpen(true)}
          >
            {t("Restore payment account")}
          </Button>
        )

      case "not_started":
      case "disabled":
        return (
          <Button
            type="primary"
            icon={<Icon type="automation_loop" style={{ filter: "brightness(0) invert(1)" }} />}
            disabled={!isEligible}
            onClick={() => setAutomateCreditsModalOpen(true)}
          >
            {t("Automate credits")}
          </Button>
        )

      default:
        return null
    }
  }, [
    automationButtonVariant,
    automationProcessors,
    automationConfirmFetching,
    automationTurnOffFetching,
    handleTurnOff,
    handleConfirmAccess,
    isEligible,
    t
  ])

  const handleAutomateCreditsSubmit = () => {
    dispatch(requestCreditAutomation())
  }

  const handleShopifyActivate = async () => {
    const result = await dispatch(confirmCreditAutomationAccess("shopify"))
    if (confirmCreditAutomationAccess.fulfilled.match(result)) {
      setShopifyActivateOpen(false)
    }
  }

  // CHA1-2905/3086: the 200 "already requested" case shows a notice instead of pretending a fresh
  // request went through; either way the modal closes once the backend has responded.
  useEffect(() => {
    if (!automationRequestResult) return
    if (automationRequestResult.alreadyRequested) {
      notification.info({
        message:
          automationRequestResult.detail ||
          t("We have already got the request. You will be notified as soon as possible."),
        ...notificationConfig
      })
    }
    setAutomateCreditsModalOpen(false)
    // CHA1-3281: for Shopify the first submit only sends the collaborator request - open the second
    // pop-up so the merchant can accept it in Shopify and activate.
    if (shopifyProcessor && !automationRequestResult.alreadyRequested) {
      setShopifyActivateOpen(true)
    }
    dispatch(creditAutomationActions.creditAutomationRequestResultReset())
  }, [automationRequestResult, shopifyProcessor])

  useEffect(() => {
    if (isAdmin) return

    setAction(
      isMobile ? (
        <Flex vertical gap={8} align="flex-end">
          {statusButton}
          <ExportDropdown
            loading={exportFetching}
            onExport={(type) => handleExport(type)}
            isAdmin={isAdminPage}
            items={exportItemsUser}
            disabled={isDemo}
            exportAsFiltered={true}
            iconButton={true}
            inHeader={true}
          />
        </Flex>
      ) : (
        statusButton
      )
    )
    return () => setAction(null)
  }, [isMobile, isAdmin, exportFetching, handleExport, isAdminPage, isDemo, statusButton])

  return (
    <div className={cls("credits-page", { "credits-page_admin": isAdmin })}>
      {!isAdmin && (
        <Banner
          status={bannerStatus}
          processor={bannerProcessor}
          onResubmit={() => setAutomateCreditsModalOpen(true)}
        />
      )}
      {/* CHA1-2900: while automation is Active, Chargeback handles proof automatically and the
          merchant is never asked for proof again - so this reminder only makes sense for every
          other status (Pending/Action needed/Lost/no automation set up). */}
      {!isAdmin &&
      bannerStatus !== "active" &&
      result?.["requires_proof_list"]?.length &&
      !externalDemoMode ? (
        <Alert
          type="warning"
          className="fullscreen-alert"
          style={{ marginBottom: 16 }}
          showIcon
          icon={<Icon type={"bell_alarm"} />}
          message={
            <>
              {t(
                "Add proof to requests marked “Requires proof” before the deadline to avoid missing credits."
              )}
              <Link
                onClick={() => handleChangeFilter({ request_status: ["Requires proof"] })}
                strong
                style={{ marginLeft: 8, zIndex: 2, position: "relative" }}
              >
                {t("Check “Requires proof” requests")}
              </Link>
            </>
          }
        />
      ) : null}

      {/* <Flex gap={!isMobile ? 24 : 8}>
        {(isAdmin ? [...statisticDataAdmin] : [...statisticData]).map((el) => (
          <FilterCard
            key={el.name}
            data={{
              ...el,
              value: statistic?.[el.id?.toLowerCase()]
            }}
            isActive={filters?.[isAdmin ? "alert_service" : "request_type"]?.includes(el.id)}
            onClick={(value) => handleSelectType(value, isAdmin ? "alert_service" : "request_type")}
            theme={theme}
            isMobile={isMobile}
          />
        ))}
      </Flex> */}

      <div className="credits-page-statistic-block" data-tour="credits-statistic-cards">
        {statisticCardsData?.map((el) => (
          <DashboardCard key={el.title} data={{ ...el }} theme={theme} />
        ))}
      </div>

      {!isMobile && <Divider />}

      <Flex
        vertical={isMobile}
        gap={isMobile ? 0 : 32}
        align={isMobile ? "flex-end" : "center"}
        justify="space-between"
        style={{
          margin: !isMobile ? "-4px 0 20px" : "4px 0 0",
          flexDirection: isMobile ? "column-reverse" : "row"
        }}
      >
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptionsModifiedByFolder}
          allFilters={allFilters}
          filtersType={"credits"}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={handleClearFilters}
          addGlobalFilter={props.addGlobalFilter}
          removeGlobalFilter={props.removeGlobalFilter}
          columnIconMap={creditsColumnIconMap}
          isMobile={isMobile}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData usersViewForAdmin={usersViewForAdmin} />
          <Segmented
            options={optionsVisibility}
            value={filters?.["request_type"]}
            onChange={(value) => handleChangeFilter({ request_type: value })}
          />

          {!isMobile && (
            <ExportDropdown
              loading={exportFetching}
              onExport={(type) => handleExport(type)}
              isAdmin={isAdminPage}
              items={isAdminPage ? exportItemsAdmin : exportItemsUser}
              disabled={isDemo || isDemoModeBlocked}
              exportAsFiltered={!isDemoModeBlocked}
            />
          )}

          {!isMobile && effectiveViewMode === "table" && (
            <ManageColumn
              pinnedColumns={isAdmin ? pinnedColumnsAdmin : pinnedColumns}
              myColumns={myColumns}
              onChangeColumns={(newColumns) => handleChangeColumns(newColumns)}
              iconsType={"credits"}
              disabled={isBusinessAccountAdministrator ? false : isDemo}
              dataTour="credits-table-manage-columns"
            />
          )}
          {!isMobile && !isAdmin && (
            <ListViewToggle view={creditsViewMode} setView={setCreditsViewMode} />
          )}
        </Space>
      </Flex>

      {!isMobile && (
        <ChangeStatusBlock selectedRequestId={selectedRequestId} options={filterOptions} />
      )}

      {effectiveViewMode === "table" && (
        <Table
          columns={getTableColumns}
          loading={fetching}
          dataSource={results}
          scroll={tableSettings.scroll}
          onChange={handleChangeTable}
          pagination={tableSettings.pagination}
          showSizeChanger={false}
          //withOutPagination={true}
          locale={tableSettings.locale}
          rowSelection={
            !isAdmin || ["completed", "archive"].includes(folder)
              ? false
              : tableSettings.rowSelection
          }
          headerDataTour="credits-table-header"
          //rowHoverable={true}
          /*onRow={(record) => ({
            onClick: (event) => {
              openAlertDetails(record)
            },
          })}*/
        />
      )}
      {effectiveViewMode === "cards" && (
        <Flex vertical gap={16}>
          <CreditsList
            data={results ?? []}
            handleSelectRow={handleSelectRow}
            loading={fetching}
            emptyComponent={
              <EmptyComponent type={isAdmin ? "requests_credits_admin" : "credits"} />
            }
          />
          {results?.length > 0 &&
            (isMobile ? (
              <MobilePagination
                current={pageNumber}
                total={count || 0}
                pageSize={pageSize}
                onChange={(page) => setPageNumber(page)}
              />
            ) : (
              <div className="custom-table" style={{ marginBottom: "15px" }}>
                <Pagination
                  rootClassName={"ant-table-pagination ant-table-pagination-left"}
                  onChange={(page) => setPageNumber(page)}
                  position={["none", "bottomLeft"]}
                  total={count || 0}
                  pageSize={pageSize}
                  current={pageNumber}
                  showLessItems={true}
                  showTotal={(total, range) => (
                    <Text>
                      Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
                    </Text>
                  )}
                  itemRender={(_, type, originalElement) =>
                    pageSize >= count ? null : itemRender(_, type, originalElement, t)
                  }
                  showSizeChanger={false}
                  showQuickJumper={false}
                />
              </div>
            ))}
        </Flex>
      )}

      <CreditRequestPreview />
      <CreditRequestModal onlyModal isDemo={isDemo} />
      <CreditDrawer setRejectModalVisible={setRejectModalVisible} />
      <RejectRequestModal
        isOpen={!!rejectModalVisible}
        setIsOpen={setRejectModalVisible}
        selectedId={rejectModalVisible}
      />
      <CreditDetailsDrawer />
      <AutomateCreditsModal
        open={automateCreditsModalOpen}
        onCancel={() => setAutomateCreditsModalOpen(false)}
        onSubmit={handleAutomateCreditsSubmit}
        processors={automationProcessors}
        requestFetching={automationRequestFetching}
      />
      <ShopifyActivateAccessModal
        open={shopifyActivateOpen}
        onCancel={() => setShopifyActivateOpen(false)}
        onActivate={handleShopifyActivate}
        activateFetching={automationConfirmFetching}
        acceptUrl={shopifyProcessor?.accept_url}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  ba_role: state.auth.userInfo?.ba_role?.id,
  ba_role_name: state.auth.userInfo?.ba_role?.name,
  isAdmin: state.auth.isAdmin,
  is_demo: state.auth.userInfo?.is_demo,
  nav: state.nav,

  visibleFiltersUser: state.credits.visibleFilters,
  visibleFiltersAdmin: state.credits.visibleFiltersAdmin,

  defaultColumns: state.credits.defaultColumns,
  defaultColumnsAdmin: state.credits.defaultColumnsAdmin,

  columns: state.credits.columns,
  columnsAdmin: state.credits.columnsAdmin,

  fetchingStatistics: state.credits.fetchingStatistics,
  statistics: state.credits.statistics,

  fetching: state.credits.fetching,
  result: state.credits.result,

  filters: state.credits.filters,
  filterOptions: state.credits.filterOptions,

  exportFetching: state.credits.exportFetching,
  exportResult: state.credits.exportResult,

  saveResult: state.credits.saveResult,
  test_data: state.globalFilters.testData,

  creditsViewMode: state.credits.creditsViewMode
})

const mapDispatchToProps = (dispatch) => ({
  getCreditsFilterOptions: (params) => dispatch(getCreditsFilterOptions(params)),
  getCreditsStatistics: (params) => dispatch(getCreditsStatistics(params)),
  getCredits: (params) => dispatch(getCredits(params)),
  updateCredit: (params) => dispatch(updateCredit(params)),
  pinCredit: (params) => dispatch(pinCredit(params)),
  deleteCredit: (id) => dispatch(deleteCredit(id)),
  getCreditsExport: (data) => dispatch(getCreditsExport(data)),
  clearExportResult: () =>
    dispatch(creditsActions.creditsSetProp({ key: "exportResult", value: null })),
  changeColumns: (key, columns) => dispatch(creditsActions.creditsSetProp({ key, value: columns })),
  changeFilters: (filters) =>
    dispatch(creditsActions.creditsSetProp({ key: "filters", value: filters })),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(creditsActions.creditsSetProp({ key: visibleFiltersType, value: filters })),
  clearCreditsFilters: (data) => dispatch(creditsActions.clearCreditsFilters(data)),
  saveResultReset: () =>
    dispatch(creditsActions.creditsSetProp({ key: "saveResult", value: null })),
  getCreditDetailsRequest: (id) => dispatch(getCreditDetails(id)),
  clearGlobalFilters: () => dispatch(clearGlobalFiltersAction()),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(userSave({ current_business_account_id: newBusinessAccountId })),
  addGlobalFilter: (key, value) => dispatch(addGlobalFilterAction({ key, value })),
  removeGlobalFilter: (key) => dispatch(removeGlobalFilterAction(key)),
  selectRequestAddComment: (request) =>
    dispatch(RequestsCreators.requestsSetProp("selectedRequest", request)),
  setCreditsViewMode: (mode) =>
    dispatch(creditsActions.creditsSetProp({ key: "creditsViewMode", value: mode }))
})

export default connect(mapStateToProps, mapDispatchToProps)(CreditsPage)
